import {
	CognitoIdToken,
	CognitoUserSession,
	CognitoAccessToken,
	CognitoRefreshToken,
	CognitoUserPool,
	CognitoUser,
	CognitoUserAttribute,
	AuthenticationDetails
} from 'amazon-cognito-identity-js';

import {
	getAccessToken,
	getRefreshToken,
	getIdToken,
	saveIdToken,
	saveAccessToken
} from '../../scanline/utils/token';

import aipurls from '../../config/configs.urls';
// eslint-disable-next-line import/no-cycle
import { getActiveCompany } from './companySwitch';

const getCognitoIdToken = () => {
	return new CognitoIdToken({ IdToken: getIdToken() });
};
const getCognitoAccessToken = () => {
	return new CognitoAccessToken({ AccessToken: getAccessToken() });
};
const getCognitoRefreshToken = () => {
	// CognitoRefreshToken constructor expects { RefreshToken } (not { AccessToken }).
	// Using the wrong key leaves this.token = '' which makes refreshSession fail
	// silently, so the cookie is never written with the updated ID token.
	return new CognitoRefreshToken({ RefreshToken: getRefreshToken() });
};

export const getUserSession = () => {
	const userSession = new CognitoUserSession({
		IdToken: getCognitoIdToken(),
		AccessToken: getCognitoAccessToken(),
		RefreshToken: getCognitoRefreshToken()
	});
	return userSession;
};

export const isTokenValid = () => {
	const userSession = getUserSession();
	return userSession.isValid();
};

export const isTokenExpired = () => {
	const userSession = getUserSession();
	const expireDate = new Date(userSession.accessToken.getExpiration() * 1000);
	return new Date() > expireDate;
};

export const getLoggedInUser = () => {
	const { payload: userData = {} } = getCognitoIdToken();
	const {
		'cognito:username': username = '',
		'cognito:groups': arrCognitoGroups = [],
		email = '',
		given_name: firstName = '',
		family_name: lastName = '',
		"custom:system_of_units": systemOfUnits = 'Metric',
		phone_number: phone = '',
		sub: uuid = '',
		customer
	} = userData;

	return {
		uuid,
		customer,
		username,
		email,
		firstName,
		lastName,
		phone,
		fullName: `${firstName} ${lastName}`.trim(),
		systemOfUnits,
		cognitoGroups: arrCognitoGroups.reduce((acc, key) => {
			acc[key] = true;
			return acc;
		}, {})
	};
};

export const getUserName = () => {
	const { username } = getLoggedInUser();
	return username;
};

export const getUserUUID = () => {
	const { uuid } = getLoggedInUser();
	return uuid;
};

export const getEmail = () => {
	const { email } = getLoggedInUser();
	return email;
};

export const getCustomer = () => {
	// An operator who has switched into a customer company sees the whole app as
	// that company; fall back to the company on their own token otherwise.
	const activeCompany = getActiveCompany();
	if (activeCompany) {
		return activeCompany;
	}
	const { customer } = getLoggedInUser();
	return customer;
};

export const getFullName = () => {
	const { fullName } = getLoggedInUser();
	return fullName;
};

export const getSystemOfUnits = () => {
	const { systemOfUnits } = getLoggedInUser();
	return systemOfUnits;
};

export const getPhone = () => {
	const { phone } = getLoggedInUser();
	return phone;
};

export const hasCognitoGroupName = groupName => {
	const { cognitoGroups = {} } = getLoggedInUser();
	return cognitoGroups[groupName];
};

const getSettingJson = settingString => {
	if (!settingString) {
		return {};
	}

	if (typeof settingString !== 'string') {
		return settingString;
	}

	try {
		return JSON.parse(settingString);
	} catch (e) {
		if (e instanceof SyntaxError) {
			return {};
		}

		throw e;
	}
};

export const getAppSettings = app => {
	const idToken = getCognitoIdToken();

	const appSettingsRaw = idToken.payload[app];
	return getSettingJson(appSettingsRaw);
};

export const isUserInGroup = groupName => {
	const idToken = getCognitoIdToken();
	const { payload: { 'cognito:groups': groups = [] } = {} } = idToken;
	return groups.indexOf(groupName) !== -1;
};

export const getUserSettings = app => {
	const idToken = getCognitoIdToken();
	const {
		payload: { [app]: setting, userSettings: strUserSettings = '{}' } = {}
	} = idToken;
	if (setting) {
		return setting;
	}
	const userSettings = getSettingJson(strUserSettings);
	const { [app]: userAppSettings = {} } = userSettings;
	return userAppSettings;
};

const getUserPool = poolData => {
	return new CognitoUserPool(poolData);
};

const getCognitoUser = (username, poolData) => {
	const userData = {
		Username: username,
		Pool: getUserPool(poolData)
	};
	return new CognitoUser(userData);
};

export const login = (authenticationData, poolData) => {
	return new Promise((resolve, reject) => {
		const { Username: username } = authenticationData;
		const cognitoUser = getCognitoUser(username, poolData);
		const authenticationDetails = new AuthenticationDetails(authenticationData);
		cognitoUser.authenticateUser(authenticationDetails, {
			onSuccess: result => {
				resolve(result);
			},
			onFailure: error => {
				reject(error);
			}
		});
	});
};

/**
 * Update editable Cognito user attributes (given_name, family_name, phone_number)
 * using the currently authenticated session.
 */
export const updateUserProfile = ({ firstName, lastName, phone } = {}) => {
	return new Promise((resolve, reject) => {
		try {
			// poolid is stored as 'cognito-idp.<region>.amazonaws.com/<UserPoolId>'
			const poolIdFull = (aipurls && aipurls.poolid) || '';
			const userPoolId = poolIdFull.includes('/')
				? poolIdFull.split('/').pop()
				: poolIdFull;
			const clientId = aipurls && aipurls.clientid;

			const pool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
			const username = getUserName();
			const cognitoUser = new CognitoUser({ Username: username, Pool: pool });

			// Attach the active authenticated session so we don't need re-login
			cognitoUser.setSignInUserSession(getUserSession());

			const attributes = [];
			if (firstName !== undefined && firstName !== null) {
				attributes.push(new CognitoUserAttribute({ Name: 'given_name', Value: String(firstName) }));
			}
			if (lastName !== undefined && lastName !== null) {
				attributes.push(new CognitoUserAttribute({ Name: 'family_name', Value: String(lastName) }));
			}
			if (phone !== undefined && phone !== null) {
				// Cognito requires E.164 format for phone_number (e.g. +12223334444)
				attributes.push(new CognitoUserAttribute({ Name: 'phone_number', Value: String(phone).trim() }));
			}

			if (attributes.length === 0) {
				resolve('No changes');
				return;
			}

			cognitoUser.updateAttributes(attributes, (err, result) => {
				if (err) { reject(err); return; }

				// Refresh the Cognito session so the new ID token contains the updated
				// attribute claims (given_name, family_name, phone_number).
				//
				// IMPORTANT: our app stores tokens in cookies (via scanline/utils/token),
				// NOT in the Cognito SDK's own localStorage keys.  After refreshSession
				// succeeds we must manually copy the new tokens into our cookies so that
				// the next getLoggedInUser() call returns fresh values without re-login.
				const currentSession = getUserSession();
				cognitoUser.refreshSession(
					currentSession.getRefreshToken(),
					(refreshErr, newSession) => {
						if (!refreshErr && newSession) {
							try {
								// Write updated tokens into the app's cookie store
								saveIdToken(newSession.getIdToken().getJwtToken());
								saveAccessToken(newSession.getAccessToken().getJwtToken());
							} catch (saveErr) {
								console.warn('Failed to persist refreshed tokens:', saveErr);
							}
						} else if (refreshErr) {
							console.warn('Session refresh after profile update failed:', refreshErr);
						}
						// Resolve regardless — attributes were saved successfully
						resolve(result);
					}
				);
			});
		} catch (e) {
			reject(e);
		}
	});
};

/**
 * Change the user's password using the currently authenticated session.
 * @param {{ currentPassword: string, newPassword: string }} params
 */
export const changePassword = ({ currentPassword, newPassword } = {}) => {
	return new Promise((resolve, reject) => {
		try {
			const poolIdFull = (aipurls && aipurls.poolid) || '';
			const userPoolId = poolIdFull.includes('/')
				? poolIdFull.split('/').pop()
				: poolIdFull;
			const clientId = aipurls && aipurls.clientid;

			const pool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
			const username = getUserName();
			const cognitoUser = new CognitoUser({ Username: username, Pool: pool });

			// Attach the active authenticated session so we don't need re-login
			cognitoUser.setSignInUserSession(getUserSession());

			cognitoUser.changePassword(currentPassword, newPassword, (err, result) => {
				if (err) reject(err);
				else resolve(result);
			});
		} catch (e) {
			reject(e);
		}
	});
};
